import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getVehiclesFromDb } from "../../api/getVehiclesFromDb";
import { getAssetErrors } from "../../api/getErrors";
import { userHasAccess } from "../../services/RoleBasedAccess";
import { State } from '../../state/reducers';
import { Account, Vehicle, Asset } from "../../state/types";
import { AssetContextType } from '../../context/AssetContext';

export interface ImageCategory {
  key: string;
  label: string;
  images: string[];
  visible?: boolean;
}

function getUndercarriageImageInfo(asset: Asset): ImageCategory {
  const hasDewarp = !!asset.imageUndercarriageDewarp?.length;
  const fallback = asset.imageUndercarriage || [];

  return {
    key: "uc",
    label: hasDewarp ? "Undercarriage" : "Raw Undercarriage",
    images: hasDewarp ? asset.imageUndercarriageDewarp : fallback,
    visible: hasDewarp || !!fallback.length
  };
}

export const useAssetState = (): AssetContextType => {
  const navigate = useNavigate();
  const { assetId, clientId } = useParams<{ assetId: string; clientId: string }>();

  const user: Account = useSelector((state: State) => state.accountState.user);
  const userLoaded = !!user && !!user.role;

  const [asset, setAsset] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<any[]>([]);
  const [visibleTypes, setVisibleTypes] = useState<{ [key: string]: boolean }>({});
  const [imageCategories, setImageCategories] = useState<ImageCategory[]>([]);
  const [isEditAssetIdModalOpen, setIsEditAssetIdModalOpen] = useState(false);
  const [isEditAssetModalOpen, setIsEditAssetModalOpen] = useState(false);

  // Effect for user access and redirection
  useEffect(() => {
    if (!userLoaded) return;
    if (!clientId || !assetId) {
      navigate("/");
      return;
    }

    const baseClientId = clientId.replace(/U\d{2}$/, '');
    const isInternalUser = userHasAccess(user?.role, ["Internal"]);

    if (!isInternalUser && !user?.system_access.includes(baseClientId)) {
      navigate("/");
    }
  }, [user, userLoaded, clientId, assetId, navigate]);

  // Effect for setting the document title
  useEffect(() => {
    if (assetId) {
      document.title = assetId.split("-")[0];
    } else {
      document.title = "Black Widow UMS";
    }
    return () => {
      document.title = "Black Widow UMS";
    };
  }, [assetId]);

  // Effect for setting the document title
  useEffect(() => {
    if (!asset) {
      setImageCategories([])
      return
    }

    const ucInfo = getUndercarriageImageInfo(asset);
    const categories: ImageCategory[] = [
      { key: "exterior", label: "Exterior", images: asset.imageExterior || [] },
      { key: "crop", label: "Cropped", images: asset.imageCrop || [] },
      { key: "featured", label: "Featured", images: asset.imageFeatured || [] },
      { key: "bgr", label: "Background Replaced", images: asset.imageBgr || [] },
      { key: "manual", label: "Manual", images: asset.imageManual || [] },
      { key: "pano", label: "Panoramic", images: asset.imagePano || [] },
      ucInfo,
    ]
    setImageCategories(categories)
  }, [asset]);

  // Effect for fetching asset data
  useEffect(() => {
    if (!clientId || !assetId) {
      navigate("/");
      return;
    }

    const getVehicleData = async () => {
      setLoading(true);
      try {
        const body = {
          page: 1,
          per_page: 1,
          system_code: clientId,
          asset_id: assetId,
          archived: true
        };

        const { vehicles: assets } = await getVehiclesFromDb(body);
        const fetchedAsset = assets[0];
        setAsset(fetchedAsset);

        const errorResponse = await getAssetErrors(clientId, assetId.split("-")[0]);
        setErrors(errorResponse.errors || []);

        // Set initial visibility of image types
        if (fetchedAsset) {
          setVisibleTypes({
            exterior: !!fetchedAsset.imageExterior?.length,
            crop: !!fetchedAsset.imageCrop?.length,
            featured: !!fetchedAsset.imageFeatured?.length,
            manual: !!fetchedAsset.imageManual?.length,
            pano: !!fetchedAsset.imagePano?.length,
            uc: !!fetchedAsset.imageUndercarriageDewarp?.length || !!fetchedAsset.imageUndercarriage?.length,
            bgr: !!fetchedAsset.imageBgr?.length,
          });
        }
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
        setAsset(null);
      } finally {
        setLoading(false);
      }
    };

    getVehicleData();
  }, [clientId, assetId, navigate]);

  return {
    asset,
    assetId,
    clientId,
    user,
    loading,
    errors,
    visibleTypes,
    setVisibleTypes,
    imageCategories,

    isEditAssetIdModalOpen,
    openEditAssetIdModal: () => setIsEditAssetIdModalOpen(true),
    closeEditAssetIdModal: () => setIsEditAssetIdModalOpen(false),

    isEditAssetModalOpen,
    openEditAssetModal: () => setIsEditAssetModalOpen(true),
    closeEditAssetModal: () => setIsEditAssetModalOpen(false),
  };
};